import { Heart, MessageCircle, Shield, Users, Sparkles, Copy, CheckCircle } from 'lucide-react';
import { useState } from 'react';
import { Reveal } from '@/components/system';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const DONATION_INFO = {
  multicaixa: '931995138',
  iban: 'AO06.0040.0000.9902.7969.1021.6',
  beneficiary: 'Timóteo Sebastião',
} as const;

function CreatorMessagePanel() {
  return (
    <div className="w-full min-w-0">
      <Reveal>
        <div className="mb-5 text-center sm:mb-6">
          <div className="glass-surface mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 sm:px-4 sm:py-2">
            <Sparkles className="h-4 w-4 text-kuiva-blue" />
            <span className="text-sm font-medium text-kuiva-blue">A minha visão</span>
          </div>
          <h2 className="mb-2 px-1 text-2xl font-bold text-foreground sm:text-3xl">Por que criei a Kuiva?</h2>
        </div>
      </Reveal>

      <Reveal delayMs={100}>
        <div className="card-kuiva relative overflow-hidden">
          <div className="absolute top-0 right-0 h-32 w-32 -translate-y-1/2 translate-x-1/2 rounded-full bg-kuiva-blue/5" />
          <div className="absolute bottom-0 left-0 h-24 w-24 translate-y-1/2 -translate-x-1/2 rounded-full bg-kuiva-green/5" />

          <div className="relative">
            <div className="mb-4 flex items-center gap-3 sm:gap-4">
              <div className="h-20 w-20 overflow-hidden rounded-full border-4 border-kuiva-green shadow-lg">
                <img
                  src="/images/timoteo-sebastiao.jpg"
                  alt="Timóteo Sebastião"
                  className="h-full w-full object-cover"
                />
              </div>
              <div>
                <p className="text-lg font-semibold text-foreground">Timóteo Sebastião</p>
                <p className="text-sm text-muted-foreground">Fundador da Kuiva</p>
              </div>
            </div>

            <div className="space-y-3 text-sm leading-relaxed text-foreground-secondary sm:text-base">
              <p>
                Criei a Kuiva porque acredito que todos merecem acesso a apoio psicológico de qualidade, sem
                barreiras. Em Angola, a saúde mental ainda é um tema cercado de estigma, e muitos jovens não têm
                onde procurar ajuda.
              </p>

              <p>
                Quis construir uma ponte entre quem precisa de apoio e profissionais qualificados, de forma simples,
                discreta e acessível. Sem burocracias, sem julgamentos.
              </p>

              <div className="my-4 grid gap-3 sm:grid-cols-2 sm:gap-3">
                <div className="glass-surface flex items-start gap-3 rounded-xl p-3">
                  <Users className="mt-0.5 h-5 w-5 flex-shrink-0 text-kuiva-blue" />
                  <div>
                    <p className="text-sm font-medium">Ligação direta</p>
                    <p className="text-xs text-muted-foreground">Psicólogos qualificados de forma rápida e discreta</p>
                  </div>
                </div>

                <div className="glass-surface flex items-start gap-3 rounded-xl p-3">
                  <MessageCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-kuiva-blue" />
                  <div>
                    <p className="text-sm font-medium">Consultas flexíveis</p>
                    <p className="text-xs text-muted-foreground">Apoio online onde e quando precisares</p>
                  </div>
                </div>

                <div className="glass-surface flex items-start gap-3 rounded-xl p-3">
                  <Heart className="mt-0.5 h-5 w-5 flex-shrink-0 text-kuiva-blue" />
                  <div>
                    <p className="text-sm font-medium">Valor acessível</p>
                    <p className="text-xs text-muted-foreground">Sessões desde 8.000 Kz</p>
                  </div>
                </div>

                <div className="glass-surface flex items-start gap-3 rounded-xl p-3">
                  <Shield className="mt-0.5 h-5 w-5 flex-shrink-0 text-kuiva-blue" />
                  <div>
                    <p className="text-sm font-medium">Confidencialidade</p>
                    <p className="text-xs text-muted-foreground">Espaço seguro e livre de julgamentos</p>
                  </div>
                </div>
              </div>

              <p>
                O acesso a profissionais de saúde mental em Angola ainda é escasso. Muitos não têm tempo, recursos ou
                coragem para procurar ajuda em hospitais. A Kuiva nasceu para mudar isso.
              </p>

              <p className="font-medium text-kuiva-blue">
                Juntos, podemos reduzir o estigma e promover o bem-estar emocional de todos os angolanos.
              </p>
            </div>

            <div className="mt-4 border-t border-border pt-4">
              <p className="text-sm italic text-muted-foreground">«Não precisas enfrentar tudo sozinho.»</p>
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delayMs={180}>
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-3">
          <div className="glass-surface rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-kuiva-blue">12+</p>
            <p className="text-xs text-muted-foreground">Profissionais</p>
          </div>
          <div className="glass-surface rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-kuiva-green">500+</p>
            <p className="text-xs text-muted-foreground">Pessoas ajudadas</p>
          </div>
          <div className="glass-surface rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-kuiva-accent">4.9</p>
            <p className="text-xs text-muted-foreground">Avaliação média</p>
          </div>
          <div className="glass-surface rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-purple-500">100%</p>
            <p className="text-xs text-muted-foreground">Confidencial</p>
          </div>
        </div>
      </Reveal>
    </div>
  );
}

function CreatorDonationsCard() {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="card-kuiva border-2 border-kuiva-blue/25 ring-1 ring-white/40">
      <div className="mb-4 text-center sm:mb-5">
        <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-kuiva-blue text-white sm:mb-4 sm:h-14 sm:w-14">
          <Heart className="h-7 w-7" />
        </div>
        <h2 className="mb-2 text-xl font-bold text-foreground sm:text-2xl">Apoia a Kuiva</h2>
        <p className="text-sm text-muted-foreground">A tua contribuição ajuda-nos a manter e expandir esta plataforma</p>
      </div>

      <div className="space-y-4">
        <div className="glass-surface rounded-xl p-4">
          <p className="mb-1 text-xs text-muted-foreground">Multicaixa Express</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <p className="break-all font-mono font-semibold text-foreground sm:break-normal">{DONATION_INFO.multicaixa}</p>
            <button
              type="button"
              onClick={() => copyToClipboard(DONATION_INFO.multicaixa, 'multicaixa')}
              className="flex shrink-0 touch-manipulation items-center gap-1.5 self-start rounded-lg bg-kuiva-light px-3 py-1.5 text-sm font-medium text-kuiva-blue transition-colors hover:bg-kuiva-blue/20 sm:self-center"
            >
              {copiedField === 'multicaixa' ? (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copiar
                </>
              )}
            </button>
          </div>
        </div>

        <div className="glass-surface rounded-xl p-4">
          <p className="mb-1 text-xs text-muted-foreground">IBAN</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
            <p className="min-w-0 flex-1 break-all font-mono text-sm font-semibold text-foreground">{DONATION_INFO.iban}</p>
            <button
              type="button"
              onClick={() => copyToClipboard(DONATION_INFO.iban, 'iban')}
              className="flex shrink-0 touch-manipulation items-center gap-1.5 self-start rounded-lg bg-kuiva-light px-3 py-1.5 text-sm font-medium text-kuiva-blue transition-colors hover:bg-kuiva-blue/20 sm:self-center"
            >
              {copiedField === 'iban' ? (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copiar
                </>
              )}
            </button>
          </div>
        </div>

        <div className="glass-surface rounded-xl p-4">
          <p className="mb-1 text-xs text-muted-foreground">Beneficiário</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <p className="min-w-0 flex-1 break-words font-semibold text-foreground">{DONATION_INFO.beneficiary}</p>
            <button
              type="button"
              onClick={() => copyToClipboard(DONATION_INFO.beneficiary, 'beneficiary')}
              className="flex shrink-0 touch-manipulation items-center gap-1.5 self-start rounded-lg bg-kuiva-light px-3 py-1.5 text-sm font-medium text-kuiva-blue transition-colors hover:bg-kuiva-blue/20 sm:self-center"
            >
              {copiedField === 'beneficiary' ? (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copiar
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <p className="mt-4 text-center text-xs text-muted-foreground">Obrigado por acreditares na nossa missão 💚</p>
    </div>
  );
}

/** Secção de doações na página inicial (entre CTA e FAQ, como antes). */
export function CreatorDonationsSection() {
  return (
    <section className="section-y bg-gradient-to-br from-kuiva-blue/[0.07] via-transparent to-kuiva-green/[0.07] px-3 backdrop-blur-[2px] sm:px-6">
      <div className="mx-auto w-full min-w-0 max-w-4xl">
        <Reveal delayMs={80}>
          <CreatorDonationsCard />
        </Reveal>
      </div>
    </section>
  );
}

/** Botão centrado (após FAQ na landing) + diálogo com a mensagem pessoal (sem doações). */
export function CreatorMessageSection() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <section className="flex justify-center px-3 py-4 sm:px-6 sm:py-5">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Abrir Mensagem do criador"
          className="max-w-[min(100vw-2rem,14rem)] rounded-2xl border border-kuiva-blue/20 bg-white/85 px-4 py-2.5 text-center text-xs font-semibold leading-tight text-kuiva-blue shadow-md backdrop-blur-md transition-[box-shadow,transform,background-color] hover:bg-white hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kuiva-blue/35 active:scale-[0.98] sm:max-w-[16rem] sm:px-5 sm:py-3 sm:text-sm"
        >
          Mensagem do criador
        </button>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton
          className="max-h-[min(90dvh,900px)] max-w-[calc(100%-1.25rem)] gap-0 overflow-y-auto border-border/80 bg-gradient-to-br from-kuiva-blue/[0.06] via-background to-kuiva-green/[0.06] p-4 shadow-xl sm:max-w-3xl sm:p-6"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>mensagem do criador</DialogTitle>
          </DialogHeader>
          <CreatorMessagePanel />
        </DialogContent>
      </Dialog>
    </>
  );
}
